import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  CardMembership as PlansIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Campaign as CampaignIcon,
  Link as LinkIcon,
  Queue as QueueIcon,
  ContactSupport as SupportIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import BrandMark from '../components/BrandMark';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 240;

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
    { text: 'Agencies', icon: <BusinessIcon />, path: '/admin/agencies' },
    { text: 'Contractors', icon: <PeopleIcon />, path: '/admin/contractors' },
    { text: 'Plans', icon: <PlansIcon />, path: '/admin/plans' },
    { text: 'Campaign Credits', icon: <CampaignIcon />, path: '/admin/campaign-credits' },
    { text: 'Domain Integration', icon: <LinkIcon />, path: '/admin/domain-integration' },
    { text: 'Queue Management', icon: <QueueIcon />, path: '/admin/queue-management' },
    { text: 'Usage Tips', icon: <CampaignIcon />, path: '/admin/usage-tips' },
    { text: 'Chat', icon: <ChatIcon />, path: '/admin/chat' },
    { text: 'Support Tickets', icon: <SupportIcon />, path: '/admin/support-tickets' },
    { text: 'Service limits & fees', icon: <ReceiptIcon />, path: '/admin/service-limits-fees' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
  ];

  const drawer = (
    <div>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BrandMark size={28} />
        <Typography variant="h6" noWrap component="div">
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Admin Panel'}
          </Typography>
          <Button color="inherit" onClick={handleLogout} sx={{ ml: 2 }}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Logout</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to log out?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { setLogoutDialogOpen(false); logout(); }}>Log out</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLayout; 